import { Button, ButtonProps, Icon, View, ViewProps, VisuallyHidden, XStack, YStack, wrapChildrenInText, H6 } from '@red-ui/components'
import React, { useEffect, useRef, RefObject, createContext, DialogHTMLAttributes, useContext } from 'react'
import './SideSheet.css'
import { getString } from '@vrw/data'

const InternalDialogContext = createContext<{
  dialogRef: RefObject<HTMLDialogElement> | null
}>({
  dialogRef: null,
})

const Root = View.styleable<DialogHTMLAttributes<HTMLDialogElement> & { containerProps?: ViewProps }>(
  ({ open, containerProps, ...props }, externalRef?: RefObject<HTMLDialogElement> | null) => {
    const _ref = useRef<HTMLDialogElement>(null)
    const ref = externalRef ?? _ref

    useEffect(() => {
      if (open) ref.current?.showModal()
      else ref.current?.close()
    }, [open, ref])

    useEffect(() => {
      const controller = new AbortController()

      if (open && ref.current)
        ref.current.addEventListener(
          'click',
          (event) => {
            if (event.target === ref.current) props.onClose?.(event as unknown as React.MouseEvent<HTMLDialogElement>)
          },
          { signal: controller.signal }
        )

      return () => controller.abort()
    }, [open, props, ref])

    return (
      <InternalDialogContext.Provider value={{ dialogRef: ref }}>
        <View
          {...(props as ViewProps)}
          width="100%"
          $gtMobile={{ width: '50%' }}
          $gtTabletLandscape={{ width: '40%' }}
          ref={ref}
          tag="dialog"
          className="sideSheet"
          display="unset"
          padding="0"
          margin="inherit"
          left="auto"
          right="0"
          top="0"
          borderRadius="$0"
          maxHeight="none"
          height="100%"
          maxWidth="none">
          <YStack height="100%" {...containerProps}>
            {props.children}
          </YStack>
        </View>
      </InternalDialogContext.Provider>
    )
  }
)

const CloseButton = Button.styleable<ButtonProps>(({ ...props }, ref) => {
  const { dialogRef } = useContext(InternalDialogContext)
  if (!dialogRef) throw new Error('SideSheet.CloseButton must be used within a SideSheet')

  return (
    <Button
      position="absolute"
      zIndex="$5"
      icon={Icon.X}
      circular
      backgroundColor="$black1"
      color="$black10"
      borderWidth="$0"
      hoverStyle={{
        backgroundColor: '$black2',
        cursor: 'pointer',
      }}
      pressStyle={{
        backgroundColor: '$black4',
      }}
      ref={ref}
      onPress={(...args) => {
        if (props.onPress) props.onPress(...args)
        else dialogRef.current?.close()
      }}
      {...props}>
      <VisuallyHidden>{getString('redSkySearch.sidesheet.button.close')}</VisuallyHidden>
    </Button>
  )
})

const Header = XStack.styleable<{ closeButton?: React.ReactElement<typeof CloseButton>; buttonPosition?: 'left' | 'right' }>(
  ({ children, closeButton, buttonPosition = 'left', ...props }, ref) => {
    return (
      <XStack
        ref={ref}
        {...props}
        gap="$2"
        padding="$3"
        borderBottomWidth={1}
        borderColor="$black1"
        flexDirection={buttonPosition === 'left' ? 'row-reverse' : 'row'}>
        <YStack flex={1} alignItems="center">
          {wrapChildrenInText(H6, { children, size: '$3', textProps: { fontWeight: 600 } })}
        </YStack>
        {closeButton ?? <CloseButton />}
      </XStack>
    )
  }
)

export const SideSheet = {
  Root,
  Header,
  CloseButton,
}
